import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Loader2, ZoomIn } from "lucide-react";

async function getCroppedBlob(src, cropPixels) {
  const image = await new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(cropPixels.width);
  canvas.height = Math.round(cropPixels.height);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, cropPixels.width, cropPixels.height
  );
  return new Promise((res) => canvas.toBlob((b) => res(b), "image/jpeg", 0.92));
}

export default function ImageCropDialog({ open, src, aspect = 1, title = "Adatta immagine", round = false, onCancel, onCropped }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [pixels, setPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onComplete = useCallback((_, p) => setPixels(p), []);

  const confirm = async () => {
    if (!pixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(src, pixels);
      await onCropped(blob);
    } finally {
      setSaving(false);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="bg-[#1A1A1A] border-white/10 text-white sm:max-w-md" data-testid="image-crop-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
          <DialogDescription className="text-white/50">Trascina per posizionare e usa lo zoom per adattare l'immagine.</DialogDescription>
        </DialogHeader>
        <div className="relative w-full h-72 bg-black rounded-xl overflow-hidden">
          {src && (
            <Cropper
              image={src}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={round ? "round" : "rect"}
              showGrid={!round}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3 mt-3">
          <ZoomIn className="w-4 h-4 text-white/50 shrink-0" />
          <Slider value={[zoom]} min={1} max={3} step={0.05} onValueChange={(v) => setZoom(v[0])} className="flex-1" data-testid="crop-zoom" />
        </div>
        <div className="flex gap-3 mt-2">
          <Button variant="outline" onClick={onCancel} className="flex-1 bg-transparent border-white/15 hover:bg-white/5">Annulla</Button>
          <Button onClick={confirm} disabled={saving || !pixels} data-testid="crop-confirm" className="flex-1 bg-[#E91E8C] hover:bg-[#D8157B]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Applica"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
